import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP } from 'components/financepro';

const formatoFecha = (f) => {
  if (!f) return '—';
  const d = new Date(f);
  return Number.isNaN(d.getTime())
    ? String(f)
    : d.toLocaleDateString('es-DO', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const btnPrimarySx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
};

const btnOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
};

/**
 * Genera HTML para ventana de impresión (misma información que la vista previa).
 */
export const buildHtmlDocumentoEntradaRecepcion = (datos = {}) => {
  const {
    items = [],
    numero = '',
    fecha = '',
    fechaVencimiento = '',
    observaciones = '',
    cliente_nombre = '',
    empresa = {},
    subtotal = 0,
    totalImpuestos = 0,
    total = 0,
  } = datos;

  const nombreEmpresa =
    typeof empresa === 'string' ? empresa : empresa?.empresa || empresa?.nombre || 'Empresa';

  const filas = items
    .map(
      (it) => `
    <tr>
      <td style="padding:8px;border:1px solid #ccc">${(it.codigo || '—').replace(/</g, '&lt;')}</td>
      <td style="padding:8px;border:1px solid #ccc">${(it.descripcion || '—').replace(/</g, '&lt;')}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:center">${(it.unidad || 'UND').replace(/</g, '&lt;')}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:right">${Number(it.cantidad || 0)}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:right">$${Number(it.precio || 0).toFixed(2)}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:right">$${Number(it.subtotal || 0).toFixed(2)}</td>
    </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8"/>
  <title>Entrada de mercancía — OC ${String(numero).replace(/</g, '')}</title>
  <style>
    body { font-family: system-ui, Segoe UI, sans-serif; margin: 24px; color: #222; }
    h1 { font-size: 1.25rem; margin: 0 0 8px; }
    .meta { font-size: 0.9rem; color: #444; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
    th { background: #f0f0f0; padding: 8px; border: 1px solid #ccc; text-align: left; }
    th.num { text-align: right; }
    .totales { margin-top: 16px; text-align: right; font-size: 0.95rem; }
    .totales div { margin: 4px 0; }
    @media print { body { margin: 12mm; } }
  </style>
</head>
<body>
  <h1>${nombreEmpresa.replace(/</g, '&lt;')}</h1>
  <p class="meta">
    <strong>Documento de entrada de mercancía</strong><br/>
    Orden de compra Nº ${String(numero).replace(/</g, '&lt;') || '—'}<br/>
    Fecha: ${formatoFecha(fecha)}${fechaVencimiento ? ` · Entrega prevista: ${formatoFecha(fechaVencimiento)}` : ''}<br/>
    Suplidor: ${(cliente_nombre || '—').replace(/</g, '&lt;')}
    ${observaciones ? `<br/>Observaciones: ${String(observaciones).replace(/</g, '&lt;')}` : ''}
  </p>
  <table>
    <thead>
      <tr>
        <th>Código</th>
        <th>Descripción</th>
        <th>Und.</th>
        <th class="num">Cant. recibida</th>
        <th class="num">Precio</th>
        <th class="num">Subtotal</th>
      </tr>
    </thead>
    <tbody>${filas || '<tr><td colspan="6" style="padding:12px;text-align:center">Sin ítems</td></tr>'}</tbody>
  </table>
  <div class="totales">
    <div>Subtotal: $${Number(subtotal).toFixed(2)}</div>
    ${Number(totalImpuestos) > 0 ? `<div>ITBIS / impuestos: $${Number(totalImpuestos).toFixed(2)}</div>` : ''}
    <div><strong>Total: $${Number(total).toFixed(2)}</strong></div>
  </div>
  <p style="margin-top:28px;font-size:0.8rem;color:#666">Documento generado desde recepción de compras — cantidades recibidas acumuladas.</p>
</body>
</html>`;
};

/**
 * Vista previa e impresión del documento de entrada de mercancía (ítems recibidos en la OC).
 */
const DocumentoEntradaRecepcion = ({ open, onClose, datos = {} }) => {
  const { showError } = useProductosMaterialContext();

  const {
    items = [],
    numero = '',
    fecha = '',
    fechaVencimiento = '',
    observaciones = '',
    cliente_nombre = '',
    empresa = {},
    subtotal = 0,
    totalImpuestos = 0,
    total = 0,
  } = datos;

  const nombreEmpresa =
    typeof empresa === 'string' ? empresa : empresa?.empresa || empresa?.nombre || 'Empresa';

  const handleImprimir = () => {
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    ventana.document.write(buildHtmlDocumentoEntradaRecepcion(datos));
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.soft,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${FP.color.border}`, pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.1rem' }}>
            Documento de entrada — OC Nº {numero || '—'}
          </Typography>
          <Button startIcon={<CloseIcon />} onClick={onClose} size="small" sx={btnOutlinedSx}>
            Cerrar
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            bgcolor: '#fff',
            borderColor: FP.color.border,
            '@media print': { boxShadow: 'none', border: 'none' },
          }}
        >
          <Box sx={{ mb: 2, pb: 2, borderBottom: 2, borderColor: FP.color.navy, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {nombreEmpresa}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, color: FP.color.textPrimary }}>
              Documento de entrada de mercancía
            </Typography>
            <Typography variant="body2" sx={{ color: FP.color.textSecondary, mt: 1 }}>
              Orden de compra Nº <strong>{numero || '—'}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: FP.color.textSecondary }}>
              Fecha: {formatoFecha(fecha)}
              {fechaVencimiento ? ` · Entrega prevista: ${formatoFecha(fechaVencimiento)}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: FP.color.textSecondary, mt: 0.5 }}>
              Suplidor: <strong>{cliente_nombre || '—'}</strong>
            </Typography>
            {observaciones ? (
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'left' }}>
                Observaciones: {observaciones}
              </Typography>
            ) : null}
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                  <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Und.
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Cant. recibida
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Precio
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Subtotal
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Sin ítems recibidos
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, idx) => (
                    <TableRow key={item.id ?? idx}>
                      <TableCell>{item.codigo || '—'}</TableCell>
                      <TableCell>{item.descripcion || '—'}</TableCell>
                      <TableCell align="center">{item.unidad || 'UND'}</TableCell>
                      <TableCell align="right">{Number(item.cantidad || 0)}</TableCell>
                      <TableCell align="right">${Number(item.precio || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${Number(item.subtotal || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 2, borderColor: FP.color.border }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography variant="body2">Subtotal: ${Number(subtotal).toFixed(2)}</Typography>
              {Number(totalImpuestos) > 0 && (
                <Typography variant="body2">ITBIS / impuestos: ${Number(totalImpuestos).toFixed(2)}</Typography>
              )}
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Total: ${Number(total).toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: FP.color.textSecondary }}>
              Cantidades mostradas corresponden al total recibido acumulado por línea en esta orden de compra.
            </Typography>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: `1px solid ${FP.color.border}` }}>
        <Button startIcon={<PrintIcon />} onClick={handleImprimir} variant="contained" sx={btnPrimarySx}>
          Imprimir
        </Button>
        <Button onClick={onClose} variant="outlined" sx={btnOutlinedSx}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentoEntradaRecepcion;
